// CourseConfiguration.js

import React, { useState, useEffect } from 'react';
import { Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import AttachmentsTab from './AttachmentsTab';
import { isAuthenticated, getUser } from '../utils/auth';
import Swal from 'sweetalert2';

function CourseConfiguration() {
  const navigate = useNavigate();
  const { id } = useParams();  // Get course ID from the URL for editing
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [course, setCourse] = useState({
    code: '',
    title: '',
    description: '',
    duration: '',
    status: '',
    remarks: '',
    trainingProviderId: ''
  });
  const [prerequisiteCourseIds, setPrerequisiteCourseIds] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [qualificationPrerequisiteIds, setQualificationPrerequisiteIds] = useState([]);
  const [allQualifications, setAllQualifications] = useState([]);

  const [isEditing, setIsEditing] = useState(false);
  const [key, setKey] = useState('details');
  const [vendors, setVendors] = useState([]);
  const loggedInUser = getUser();
  console.log ("loggedinuser ", loggedInUser);

  useEffect(() => {
    if (id) {
      // If the ID exists, fetch the course details for editing
      setIsEditing(true);
      fetch(`${API_BASE_URL}/courses/get_courses.php?id=${id}`, { credentials: 'include' })
        .then(response => response.json())
        .then(data => {
          setCourse({
            code: data.code,
            title: data.title,
            description: data.description,
            duration: data.duration,
            status: data.status,
            remarks: data.remarks,
            trainingProviderId: data.training_provider_id || ''
          });
          setPrerequisiteCourseIds(Array.isArray(data.prerequisite_course_ids) ? data.prerequisite_course_ids : []);
          setQualificationPrerequisiteIds(Array.isArray(data.qualification_prerequisite_ids) ? data.qualification_prerequisite_ids : []);
        })
        .catch(error => console.error('Error fetching course:', error));
    }
  }, [id]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/companies/get_companies.php`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setVendors(data.filter(c => c.company_type === 'external'));
        }
      })
      .catch(error => console.error('Error fetching vendors:', error));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/courses/get_courses.php`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAllCourses(data);
        }
      })
      .catch(error => console.error('Error fetching courses:', error));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/qualifications/get_qualifications.php`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAllQualifications(data);
        }
      })
      .catch(error => console.error('Error fetching qualifications:', error));
  }, []);

  const handlePrerequisiteToggle = (courseId) => {
    const numericId = Number(courseId);
    setPrerequisiteCourseIds(prev =>
      prev.map(Number).includes(numericId) ? prev.filter(cid => Number(cid) !== numericId) : [...prev, numericId]
    );
  };

  const handleQualificationPrerequisiteToggle = (qualificationId) => {
    const numericId = Number(qualificationId);
    setQualificationPrerequisiteIds(prev =>
      prev.map(Number).includes(numericId) ? prev.filter(qid => Number(qid) !== numericId) : [...prev, numericId]
    );
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setCourse(prevCourse => ({
      ...prevCourse,
      [name]: value
    }));
  };

  // Immediate feedback only — the server is still the source of truth,
  // this just avoids a round-trip for the common case using the course
  // list already loaded for the prerequisite checkboxes.
  const isCodeTaken = course.code.trim() !== '' && allCourses.some(c =>
    String(c.id) !== String(id) && c.code.trim().toLowerCase() === course.code.trim().toLowerCase()
  );

  const handleSave = () => {
    if (isCodeTaken) {
      Swal.fire({ icon: 'error', text: 'Course code already exists.' });
      return;
    }
    const formData = new FormData();
    formData.append('code', course.code);
    formData.append('title', course.title);
    formData.append('description', course.description);
    formData.append('duration', course.duration);
    prerequisiteCourseIds.forEach(cid => formData.append('prerequisiteCourseIds[]', cid));
    qualificationPrerequisiteIds.forEach(qid => formData.append('qualificationPrerequisiteIds[]', qid));
    formData.append('status', course.status);
    formData.append('remarks', course.remarks);
    formData.append('trainingProviderId', course.trainingProviderId);

    console.log('Course data:', course);
    console.log('FormData content:', [...formData.entries()]);

    const apiUrl = isEditing
      ? `${API_BASE_URL}/courses/update_course.php?id=${id}`
      : `${API_BASE_URL}/courses/save_course.php`;

    fetch(apiUrl, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        // Handle empty or invalid JSON responses gracefully
        return response.text().then(text => {
          try {
            return JSON.parse(text);
          } catch (error) {
            console.log('text:', text);
            throw new Error('Invalid JSON response');
          }
        });
      })
      .then(data => {
        console.log('Data:', data);
        if (data.success) {
          navigate('/training-provider/course-list');
        } else {
          Swal.fire({ icon: 'error', text: 'Failed to save course: ' + data.message });
        }
      })
      .catch(error => {
        console.error('Error:', error);
        Swal.fire({ icon: 'error', text: 'An error occurred: ' + error.message });
      });
  }; 

  const handleCancel = () => {
    // Redirect back to the dashboard without saving
    navigate('/training-provider/course-list');
  };

  return (
    <div>
      <h2>Add/Edit Course Configuration</h2>
      <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-3">
        <Tab eventKey="details" title="Course Details" className='ml-2'>
      <Form>
        <Form.Group controlId="code">
          <Form.Label>Code</Form.Label>
          <Form.Control
            type="text"
            name="code"
            value={course.code}
            required
            isInvalid={isCodeTaken}
            onChange={handleChange}
          />
          <Form.Control.Feedback type="invalid">
            This course code is already in use.
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="title" className='mt-2'>
          <Form.Label>Name/Title</Form.Label>
          <Form.Control type="text" name="title" value={course.title} required onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="description" className='mt-2'>
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" rows={3} name="description" value={course.description} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="duration" className='mt-2'>
          <Form.Label>Duration (hours)</Form.Label>
          <Form.Control type="number" name="duration" value={course.duration} required onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="preRequisite" className='mt-2'>
          <Form.Label>Prerequisite course(s)</Form.Label>
          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '0.5rem' }}>
            {allCourses.filter(c => String(c.id) !== String(id)).map(c => (
              <Form.Check
                key={c.id}
                type="checkbox"
                id={`prereq-${c.id}`}
                label={`${c.code} — ${c.title}`}
                checked={prerequisiteCourseIds.map(Number).includes(Number(c.id))}
                onChange={() => handlePrerequisiteToggle(c.id)}
              />
            ))}
            {allCourses.length === 0 && <span className="text-muted">No other courses available yet</span>}
          </div>
        </Form.Group>
        <Form.Group controlId="qualificationPreRequisite" className='mt-2'>
          <Form.Label>Prerequisite Qualification(s)</Form.Label>
          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '0.5rem' }}>
            {allQualifications.map(q => (
              <Form.Check
                key={q.id}
                type="checkbox"
                id={`qual-prereq-${q.id}`}
                label={`${q.name} (${q.type})`}
                checked={qualificationPrerequisiteIds.map(Number).includes(Number(q.id))}
                onChange={() => handleQualificationPrerequisiteToggle(q.id)}
              />
            ))}
            {allQualifications.length === 0 && <span className="text-muted">No qualifications/roles configured yet</span>}
          </div>
        </Form.Group>
        <Form.Group controlId="status" className='mt-2'>
          <Form.Label>Status</Form.Label>
          <Form.Control as="select" name="status" value={course.status} required onChange={handleChange}>
            <option value="">Select status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </Form.Control>
        </Form.Group>
        <Form.Group controlId="remarks" className='mt-2'>
          <Form.Label>Remarks</Form.Label>
          <Form.Control as="textarea" rows={2} name="remarks" value={course.remarks} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="trainingProviderId" className='mt-2'>
          <Form.Label>Training Vendor</Form.Label>
          <Form.Control as="select" name="trainingProviderId" value={course.trainingProviderId} onChange={handleChange}>
            <option value="">Select vendor</option>
            {vendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </Form>
      <div className="mt-3">
        <Button variant="primary" className="mr-2" onClick={handleSave}>Save</Button>
        <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
      </div>
      </Tab>
      <Tab eventKey="documents" title="Documents/Attachment">
        {id !== null && (
        <AttachmentsTab 
          categoryId={id} 
          category="course_documents" 
          currentUserId={loggedInUser.id}
        />
      )}
      </Tab>
    </Tabs>
    </div>
  );
}

export default CourseConfiguration;
